import React from "react";
import { Box, Group, SegmentedControl, Stack, Text } from "@mantine/core";
import { SlotsEditor, type Slot } from "./SlotsEditor";

/** Weekday key — three-letter form so JSON payloads + form values
 *  match the ``Tenant.opening_hours`` shape used by ``TenantForm``. */
export type Weekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

/** Per-day list of slots. Empty list means the tenant is closed
 *  that day; absent key (``undefined``) means "unspecified" — the
 *  ``Unspecified`` preset renders no per-day editor at all. */
export type SlotsByDay = Partial<Record<Weekday, Slot[]>>;

/** Tri-state opening override. ``null``/``undefined`` follows the
 *  schedule (the default), ``true`` = force open, ``false`` = force
 *  closed. Surfaced as a banner above the per-day editor. */
export type OverrideValue = boolean | null | undefined;

export interface WeeklyScheduleEditorProps {
  /** Per-day slot lists. */
  value: SlotsByDay;
  onChange: (next: SlotsByDay) => void;
  /** Optional tri-state override + 24h expiry hint shown under the
   *  SegmentedControl. Both surfaces (staff TenantForm + tenant
   *  portal settings) use the same control. Pass ``undefined`` here
   *  to hide the override section entirely. */
  overrideValue?: OverrideValue;
  onOverrideChange?: (next: OverrideValue) => void;
  /** ISO timestamp; rendered as "expires <local time>" hint when the
   *  override is currently active. Falsy = no hint. */
  overrideUntil?: string | null;
  disabled?: boolean;
}

const WEEKDAYS: { key: Weekday; label: string }[] = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const officeWeekdays = (open: string, close: string): SlotsByDay =>
  Object.fromEntries(
    (["mon", "tue", "wed", "thu", "fri"] as Weekday[]).map((d) => [
      d,
      [{ start: open, end: close, available: true }],
    ]),
  ) as SlotsByDay;

const allWeekSame = (open: string, close: string): SlotsByDay =>
  Object.fromEntries(
    WEEKDAYS.map((d) => [d.key, [{ start: open, end: close, available: true }]]),
  ) as SlotsByDay;

const PRESETS: Record<string, SlotsByDay> = {
  unset: {},
  office_9_17: officeWeekdays("09:00", "17:00"),
  office_8_18: officeWeekdays("08:00", "18:00"),
  coworking: officeWeekdays("07:00", "22:00"),
  always: allWeekSame("00:00", "23:59"),
  closed: Object.fromEntries(WEEKDAYS.map((d) => [d.key, []])) as SlotsByDay,
};

/** Equality check that distinguishes "absent" (unset preset) from
 *  "empty list" (closed preset). Without it ``Object.entries`` order
 *  makes ``closed`` resolve as ``unset`` because both reduce to
 *  every-day-empty after a default-on fallback. */
const sameMap = (a: SlotsByDay, b: SlotsByDay): boolean => {
  const norm = (m: SlotsByDay) =>
    JSON.stringify(WEEKDAYS.map((d) => [d.key, d.key in m ? m[d.key] : null]));
  return norm(a) === norm(b);
};

const PRESET_OPTIONS = [
  { value: "unset", label: "Unspecified" },
  { value: "office_9_17", label: "Office 9–17" },
  { value: "office_8_18", label: "Office 8–18" },
  { value: "coworking", label: "Coworking 7–22" },
  { value: "always", label: "24/7" },
  { value: "closed", label: "Closed" },
];

/** Shared weekly-schedule editor — extracted from the staff
 *  ``TenantForm`` so the tenant portal can render the exact same UI.
 *  Combines:
 *    - Tri-state ``opening_override`` SegmentedControl (auto / force
 *      open / force closed). The host wires this to ``Tenant.opening_override``.
 *    - Preset SegmentedControl (Office 9–17, 8–18, Coworking 7–22,
 *      24/7, Closed, Custom) that snaps every weekday to a known
 *      ``SlotsByDay`` shape, plus a hidden ``custom`` entry that
 *      activates the moment the operator hand-edits any day.
 *    - Per-day ``SlotsEditor`` cards (when not in the ``unset``
 *      preset) for multi-slot per-day editing (e.g. lunch break
 *      blocked window inside a wider open slot).
 */
export const WeeklyScheduleEditor: React.FC<WeeklyScheduleEditorProps> = ({
  value,
  onChange,
  overrideValue,
  onOverrideChange,
  overrideUntil,
  disabled = false,
}) => {
  const matchedPreset = (() => {
    for (const [key, preset] of Object.entries(PRESETS)) {
      if (sameMap(value, preset)) return key;
    }
    return "custom";
  })();
  const setDay = (day: Weekday, slots: Slot[]) => {
    onChange({ ...value, [day]: slots });
  };

  const overrideEnabled = onOverrideChange !== undefined;
  const overrideKey =
    overrideValue === null || overrideValue === undefined
      ? "auto"
      : overrideValue
      ? "open"
      : "closed";
  const overrideStateLabel = (() => {
    if (overrideValue === true) return "forced open";
    if (overrideValue === false) return "forced closed";
    return null;
  })();
  const until = overrideUntil ? new Date(overrideUntil) : null;
  const stillActive = until && until.getTime() > Date.now();

  return (
    <Stack gap="md">
      {overrideEnabled && (
        <Box
          p="sm"
          style={{
            borderRadius: "var(--mantine-radius-md)",
            background: "var(--mantine-color-default-hover)",
          }}
        >
          <Stack gap={6}>
            <Box>
              <Text fz="sm" fw={500}>Manual override</Text>
              <Text fz="xs" c="dimmed">
                One-off flip for today — holidays, surprise closure, special event. Takes precedence over the schedule
                below and automatically resets to <strong>Auto</strong> after 24 hours, so you don&apos;t have to
                remember to switch it back.
              </Text>
            </Box>
            <SegmentedControl
              size="sm"
              value={overrideKey}
              onChange={(v) => {
                if (v === "auto") onOverrideChange!(null);
                else if (v === "open") onOverrideChange!(true);
                else onOverrideChange!(false);
              }}
              disabled={disabled}
              data={[
                { value: "auto", label: "Auto · follow schedule" },
                { value: "open", label: "Force open" },
                { value: "closed", label: "Force closed" },
              ]}
            />
            {overrideStateLabel && stillActive && (
              <Text fz="xs" c="dimmed">
                Currently {overrideStateLabel}; expires {until!.toLocaleString()}.
              </Text>
            )}
          </Stack>
        </Box>
      )}

      <Box>
        <Text fz="sm" fw={500}>Weekly schedule</Text>
        <Text fz="xs" c="dimmed">
          Pick a preset or build a custom week. Add a blocked slot inside an open day to carve out a lunch break or any
          other closed window.
        </Text>
      </Box>
      <SegmentedControl
        size="xs"
        value={matchedPreset}
        onChange={(v) => {
          if (v === "custom") return;
          onChange(PRESETS[v] || {});
        }}
        disabled={disabled}
        data={[
          ...PRESET_OPTIONS,
          { value: "custom", label: "Custom", disabled: matchedPreset !== "custom" },
        ]}
      />
      {matchedPreset !== "unset" && (
        <Stack gap="xs">
          {WEEKDAYS.map((d) => {
            const slots = value[d.key] ?? [];
            return (
              <Box
                key={d.key}
                p="sm"
                style={{
                  borderRadius: "var(--mantine-radius-md)",
                  border: "1px solid var(--mantine-color-default-border)",
                }}
              >
                <Group justify="space-between" mb={6}>
                  <Text fz="sm" fw={500}>{d.label}</Text>
                  <Text fz="xs" c="dimmed">
                    {slots.length === 0 ? "Closed" : `${slots.length} ${slots.length === 1 ? "slot" : "slots"}`}
                  </Text>
                </Group>
                <SlotsEditor
                  value={slots}
                  onChange={(next) => setDay(d.key, next)}
                  disabled={disabled}
                />
              </Box>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};
